import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  IconButton,
  MenuItem,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useQuery } from '@apollo/client';
import { AdventuresDocument } from '@flashpack/graphql';
import { countryFromCode } from 'src/shared/countries/countryUtils';
import { useNavigate } from 'react-router-dom';
import { RoutePath } from 'src/shared/routePath';

export const AdventureSearch: FC = () => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { data } = useQuery(AdventuresDocument);
  const navigate = useNavigate();

  const adventures = useMemo(() => {
    return (data?.adventures ?? []).map(({ code, ...rest }) => {
      const countries = code.split('_').slice(0, -1);
      const countryNames = countries.map((c) => countryFromCode(c)?.name);
      return { code, countryNames, ...rest };
    });
  }, [data?.adventures]);

  const matches = (value: string, adv: (typeof adventures)[number]) => {
    const q = value.toLowerCase();
    return (
      adv.bookingEngineTourCode.toLowerCase().includes(q) ||
      (adv.title ?? '').toLowerCase().includes(q) ||
      adv.countryNames.some((name) => name?.toLowerCase().includes(q))
    );
  };

  const filtered = search ? adventures.filter((a) => matches(search, a)) : adventures;

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  const close = () => {
    setSearch('');
    setOpen(false);
  };

  const handleSelect = (id: string) => {
    navigate(RoutePath.ADVENTURE_ITINERARIES.generatePath(id));
    close();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && filtered.length === 1) {
      event.preventDefault();
      event.stopPropagation();
      handleSelect(filtered[0].id);
    }
  };

  return (
    <>
      <IconButton
        aria-label="search adventures"
        onClick={() => {
          setOpen(true);
        }}
        data-testid="open-adventure-search"
      >
        <SearchIcon htmlColor="black" />
      </IconButton>
      <Dialog open={open} onClose={close} maxWidth="sm" fullWidth>
        <Box p={2}>
          <OutlinedInput
            inputRef={inputRef}
            fullWidth
            placeholder="Search adventures..."
            endAdornment={<SearchIcon />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{ borderRadius: '2px' }}
          />
          <Stack mt={2} spacing={1} maxHeight={300} sx={{ overflowY: 'auto' }}>
            {filtered.map((adv) => (
              <MenuItem
                key={adv.id}
                onClick={() => handleSelect(adv.id)}
                data-testid="adventure-search-option"
              >
                <Box>
                  <Typography variant="bodySingle">
                    {adv.title || adv.countryNames.join(' - ')}
                  </Typography>
                  <Typography variant="captionSingle" color="text.secondary">
                    {adv.bookingEngineTourCode}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Stack>
        </Box>
      </Dialog>
    </>
  );
};
